"use client";

import { useTranslations } from "next-intl";

import {
  feePerMonthPercent,
  FORECAST_HORIZON_MONTHS,
  monthsToRepay,
} from "@/lib/pricing-calculator/model";
import type { SimulationResult } from "@/lib/pricing-calculator/model";
import type { CalculatorState } from "@/lib/pricing-calculator/url-state";
import { cn } from "@/lib/utils";

import { useCalculatorEnvironment } from "./context";
import { Card } from "./controls";

interface CapComparisonProps {
  state: CalculatorState;
  withCap: SimulationResult;
  withoutCap: SimulationResult;
  className?: string;
  stack?: boolean;
}

function Column({
  title,
  accent,
  rows,
}: {
  title: string;
  accent: boolean;
  rows: { label: string; value: string }[];
}) {
  return (
    <div
      className={cn(
        "rounded-ds-xl flex flex-col gap-4 p-4 md:p-5",
        accent ? "bg-pulse-50" : "bg-grey-50"
      )}
    >
      <p
        className={cn(
          "text-ds-label-lg font-medium",
          accent ? "text-pulse-700" : "text-grey-600"
        )}
      >
        {title}
      </p>
      <dl className="flex flex-col gap-3">
        {rows.map((row) => (
          <div
            key={row.label}
            className="flex items-baseline justify-between gap-3"
          >
            <dt className="text-ds-body-sm text-grey-600">{row.label}</dt>
            <dd className="text-ds-body-md text-grey-950 font-medium tabular-nums">
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

const highest = (result: SimulationResult) =>
  Math.max(0, ...result.months.map((month) => month.payment));

/**
 * Side-by-side view of the same forecast with and without the monthly cap,
 * compared on fee per month rather than annualised rate.
 */
export function CapComparison({
  state,
  withCap,
  withoutCap,
  className,
  stack = false,
}: CapComparisonProps) {
  const t = useTranslations("pages.pricing.calculator.cap");
  const { format } = useCalculatorEnvironment();

  const feeWith = feePerMonthPercent(
    state.feePercent,
    withCap,
    "cash-advance",
    state.termMonths
  );
  const feeWithout = feePerMonthPercent(
    state.feePercent,
    withoutCap,
    "cash-advance",
    state.termMonths
  );
  const monthsWith = monthsToRepay(withCap, "cash-advance", state.termMonths);
  const monthsWithout = monthsToRepay(
    withoutCap,
    "cash-advance",
    state.termMonths
  );

  const unavailable = "—";
  const monthsText = (months: number | null) =>
    months === null
      ? unavailable
      : t("months", { count: format.number(months, 1) });
  const feeText = (fee: number | null) =>
    fee === null ? unavailable : format.percent(fee, 2);

  const capBinds = withCap.months.some((month) => month.capHit);

  const summary = (() => {
    if (!capBinds) {
      return t("notBinding", {
        cap: format.money(state.monthlyCap),
        highest: format.money(highest(withoutCap)),
      });
    }
    if (
      withCap.complete &&
      withoutCap.complete &&
      withCap.days !== null &&
      withoutCap.days !== null
    ) {
      const extra = withCap.days - withoutCap.days;
      if (extra <= 0) return t("sameDate");
      return t("extends", {
        days: format.number(extra),
        with: feeText(feeWith),
        without: feeText(feeWithout),
      });
    }
    if (!withCap.complete && withoutCap.complete && withoutCap.days !== null) {
      return t("capBlocks", {
        count: FORECAST_HORIZON_MONTHS,
        days: format.number(withoutCap.days),
      });
    }
    return t("bothBlocked", { count: FORECAST_HORIZON_MONTHS });
  })();

  return (
    <Card
      className={cn("flex flex-col gap-6", className)}
      stack={stack}
      data-testid="pricing-cap-comparison"
    >
      <div className="flex flex-col gap-2">
        <h3 className="text-ds-heading-h5 text-grey-950">{t("title")}</h3>
        <p className="text-ds-body-sm text-grey-600">{t("description")}</p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Column
          title={t("withCap")}
          accent
          rows={[
            { label: t("feePerMonth"), value: feeText(feeWith) },
            { label: t("timeToRepay"), value: monthsText(monthsWith) },
            { label: t("highestMonth"), value: format.money(highest(withCap)) },
          ]}
        />
        <Column
          title={t("withoutCap")}
          accent={false}
          rows={[
            { label: t("feePerMonth"), value: feeText(feeWithout) },
            { label: t("timeToRepay"), value: monthsText(monthsWithout) },
            {
              label: t("highestMonth"),
              value: format.money(highest(withoutCap)),
            },
          ]}
        />
      </div>

      <p className="text-ds-body-sm text-grey-700">
        {summary} {t("sameTotal", { total: format.money(withCap.total) })}
      </p>
    </Card>
  );
}
